import axios from "axios";
import { useEffect, useRef, useState } from "react";

import {
  CheckboxFormField,
  Form,
  FormErrorMessage,
  FormField,
  InputOTPFormField,
  useFormState,
} from "../form";
import { Alert, Button, linkVariants, toast } from "ui";
import Logo from "../../components/Icons/Logo";
import { parseErrorsToArray } from "../../utils/parseErrors";
import type { LoginResponse } from "./types";
import { getEmailFingerprint } from "../../utils/getEmailFingerprint";

type AuthenticationResponse = Extract<
  LoginResponse["data"],
  { otp_required: false }
>;

export function OTPForm({
  deviceToken,
  userEmail,
  onAuthenticate,
}: {
  deviceToken: string;
  userEmail: string;
  onAuthenticate: (values: AuthenticationResponse) => void;
}) {
  const [showManyFailureWarning, setShowManyFailureWarning] = useState(false);

  return (
    <>
      <div>
        <Logo />
        <h1 className="mt-10 text-2xl font-medium text-foundations-boldest">
          We&apos;ve emailed you a code
        </h1>
      </div>

      <div className="mt-6">
        <Form
          onSubmit={({ otp_code, trust_device }) => {
            const fingerprint = getEmailFingerprint(userEmail);
            return axios
              .post<{ data: AuthenticationResponse }>(
                `${process.env.NEXT_PUBLIC_API_BASE}/users/verify-otp/`,
                {
                  otp_code,
                  trust_device,
                  fingerprint,
                  device_token: deviceToken,
                }
              )
              .then((res) => onAuthenticate(res.data.data))
              .catch((err) => {
                if (err.status === 403) {
                  setShowManyFailureWarning(true);
                  return;
                }
                const [errorMessage] = parseErrorsToArray(err);
                return {
                  errors: {
                    otp_code: errorMessage,
                  },
                };
              });
          }}
        >
          {showManyFailureWarning && (
            <Alert
              state="warning"
              contrast="low"
              title="Too many failure attempts!"
              content={
                <p className="text-sm text-foundations-boldest">
                  Currently unavailable. Try again in a while or contact your
                  organization admin for support.
                </p>
              }
              className="mb-4 max-w-full items-start"
            />
          )}

          <p className="text-sm font-medium text-foundations-subtle">
            Enter the code you received via email: {userEmail}
          </p>
          <FormField
            label="OTP"
            name="otp_code"
            required
            validation="required"
            className="mb-6 mt-4"
          >
            <InputOTPFormField
              className="gap-4"
              slotClassName="flex-1 h-16 text-xl rounded-lg"
            />
            <FormErrorMessage className="mt-2" />
          </FormField>

          {!showManyFailureWarning && (
            <div className="mb-4 flex items-center justify-between">
              <FormField
                label="Remember this device for 30 days."
                name="trust_device"
              >
                <CheckboxFormField />
              </FormField>

              <ResendOTP
                deviceToken={deviceToken}
                onTooManyFailure={() => setShowManyFailureWarning(true)}
              />
            </div>
          )}

          <FormActionButton disabled={showManyFailureWarning} />
        </Form>
      </div>
    </>
  );
}

const FormActionButton = ({ disabled }: { disabled?: boolean }) => {
  const { isSubmitting } = useFormState();
  return (
    <Button
      variant="primary"
      type="submit"
      className="w-full"
      isLoading={isSubmitting}
      disabled={disabled}
    >
      Confirm
    </Button>
  );
};

const ResendOTP = ({
  deviceToken,
  onTooManyFailure,
}: {
  deviceToken: string;
  onTooManyFailure: () => void;
}) => {
  const [availableIn, setAvailableIn] = useState(60);
  const timerRef = useRef<NodeJS.Timer>();

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setAvailableIn((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(timerRef.current);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, []);

  if (availableIn) {
    return (
      <p className="text-sm font-medium tabular-nums text-foundations-bolder">
        Resend available in <span className="text-danger">{availableIn}</span>{" "}
        Secs
      </p>
    );
  }

  return (
    <button
      type="button"
      className={linkVariants()}
      onClick={() => {
        axios
          .post(`${process.env.NEXT_PUBLIC_API_BASE}/users/resend-otp/`, {
            device_token: deviceToken,
          })
          .then(() => {
            toast.success("A new code has been sent to your email.");
            clearInterval(timerRef.current);
            setAvailableIn(60);
            timerRef.current = setInterval(() => {
              setAvailableIn((prev) => {
                const next = prev - 1;
                if (next <= 0) {
                  clearInterval(timerRef.current);
                  return 0;
                }
                return next;
              });
            }, 1000);
          })
          .catch((err) => {
            if (err.status === 403) {
              onTooManyFailure();
            }
            const [errorMessage] = parseErrorsToArray(err);
            toast.error(errorMessage || "Failed to send OTP!");
          });
      }}
    >
      Resend code
    </button>
  );
};
